import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Crown, LogOut, Bell, Brain, AlertTriangle, Shield, TrendingUp,
  Activity, Eye, Radar, Send, RefreshCw, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const AIMonitoring = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [queryType, setQueryType] = useState('forecast');
  const [queryData, setQueryData] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [recentQueries, setRecentQueries] = useState([]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const queryTypes = [
    { value: 'forecast', label: 'Economic Forecast', icon: <TrendingUp className="h-4 w-4" />, description: 'AI-powered economic predictions and trends' },
    { value: 'fraud_detection', label: 'Fraud Detection', icon: <AlertTriangle className="h-4 w-4" />, description: 'Analyze transactions for suspicious patterns' },
    { value: 'aml_check', label: 'AML Compliance', icon: <Shield className="h-4 w-4" />, description: 'Anti-money laundering risk assessment' },
    { value: 'risk_assessment', label: 'Risk Assessment', icon: <Activity className="h-4 w-4" />, description: 'Banking sector health and systemic risks' }
  ];

  const handleQuery = async () => {
    setLoading(true);
    setResponse(null);
    
    try {
      const result = await axios.post(`${API}/ai/monitor`, {
        query_type: queryType,
        data: queryData ? { user_context: queryData } : null
      }, { headers, withCredentials: true });
      
      setResponse(result.data);
      setRecentQueries(prev => [{
        type: queryType,
        timestamp: new Date().toISOString(),
        preview: result.data.analysis.substring(0, 100) + '...'
      }, ...prev.slice(0, 4)]);
      
      toast.success('Analysis complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  // Mock system metrics
  const systemMetrics = [
    { label: 'Transactions Monitored', value: '15.4M', status: 'normal' },
    { label: 'Alerts Today', value: '23', status: 'warning' },
    { label: 'False Positive Rate', value: '0.02%', status: 'normal' },
    { label: 'System Latency', value: '45ms', status: 'normal' }
  ];

  const recentAlerts = [
    { type: 'fraud', severity: 'high', message: 'Unusual velocity detected in merchant account', time: '5 min ago' },
    { type: 'aml', severity: 'medium', message: 'Large cross-border transfer flagged for review', time: '12 min ago' },
    { type: 'fraud', severity: 'low', message: 'Multiple failed authentication attempts', time: '25 min ago' }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="ai-monitoring">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-600 rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-white">Royal CBDC</span>
              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded font-mono uppercase tracking-wider animate-pulse">
                AI War Room
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/government">
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                  Back to Dashboard
                </Button>
              </Link>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white">
                <Bell className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-300 hover:text-white" data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center">
            <Brain className="h-6 w-6 text-blue-400" />
          </div>
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-light text-white">
              AI Financial Monitoring Center
            </h1>
            <p className="text-slate-400 mt-1">GPT-5.2 powered analysis and threat detection</p>
          </div>
        </div>

        {/* System Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {systemMetrics.map((metric, idx) => (
            <Card key={idx} className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">{metric.label}</span>
                  <div className={`w-2 h-2 rounded-full ${
                    metric.status === 'normal' ? 'bg-green-500' : 'bg-yellow-500'
                  }`} />
                </div>
                <p className="font-mono text-2xl font-semibold text-white" data-testid={`ai-metric-${idx}`}>
                  {metric.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Query Panel */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="font-sans text-lg text-white flex items-center gap-2">
                  <Radar className="h-5 w-5 text-blue-400" />
                  AI Analysis Console
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Run AI-powered queries on financial data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {queryTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setQueryType(type.value)}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        queryType === type.value 
                          ? 'border-blue-500 bg-blue-500/10' 
                          : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                      }`}
                      data-testid={`query-type-${type.value}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={queryType === type.value ? 'text-blue-400' : 'text-slate-400'}>
                          {type.icon}
                        </div>
                        <span className={`font-medium ${queryType === type.value ? 'text-blue-400' : 'text-slate-300'}`}>
                          {type.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{type.description}</p>
                    </button>
                  ))}
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-slate-400">Additional Context (Optional)</label>
                  <Textarea
                    value={queryData}
                    onChange={(e) => setQueryData(e.target.value)}
                    placeholder="Enter specific data or context for the analysis..."
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 min-h-[100px]"
                    data-testid="query-context-input"
                  />
                </div>

                <Button 
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                  onClick={handleQuery}
                  disabled={loading}
                  data-testid="run-analysis-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Run Analysis
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Response Panel */}
            {response && (
              <Card className="bg-slate-900 border-slate-800" data-testid="ai-response">
                <CardHeader>
                  <CardTitle className="font-sans text-lg text-white flex items-center gap-2">
                    <Eye className="h-5 w-5 text-green-400" />
                    Analysis Results
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    {queryTypes.find(t => t.value === response.query_type)?.label}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-invert max-w-none">
                    <div className="p-4 rounded-lg bg-slate-800 border border-slate-700 whitespace-pre-wrap text-sm text-slate-300 leading-relaxed">
                      {response.analysis}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Alerts Panel */}
          <div className="space-y-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="font-sans text-lg text-white flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  Recent Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentAlerts.map((alert, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${
                        alert.severity === 'high' ? 'border-red-500/30 bg-red-500/10' :
                        alert.severity === 'medium' ? 'border-yellow-500/30 bg-yellow-500/10' :
                        'border-slate-700 bg-slate-800/50'
                      }`}
                      data-testid={`alert-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-mono uppercase ${
                          alert.severity === 'high' ? 'text-red-400' :
                          alert.severity === 'medium' ? 'text-yellow-400' :
                          'text-slate-400'
                        }`}>
                          {alert.type}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          alert.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                          alert.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-slate-700 text-slate-400'
                        }`}>
                          {alert.severity}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300">{alert.message}</p>
                      <p className="text-xs text-slate-500 mt-1">{alert.time}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="font-sans text-lg text-white flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-400" />
                  Recent Queries
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentQueries.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No queries yet</p>
                ) : (
                  <div className="space-y-3">
                    {recentQueries.map((query, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-slate-800 border border-slate-700">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-blue-400 uppercase">{query.type}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(query.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 line-clamp-2">{query.preview}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border-blue-500/30">
              <CardContent className="p-6 text-center">
                <Brain className="h-10 w-10 text-blue-400 mx-auto mb-4" />
                <p className="font-mono text-sm text-slate-300 uppercase tracking-wider mb-2">
                  AI Engine Status
                </p>
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span className="text-green-400 font-semibold">Online</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">GPT-5.2 • Emergent Integration</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIMonitoring;
